import { Box, Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { cloneDeep, isEqual, omit } from "lodash";
import { useMemo, useState } from "react";
import LoadingOverlay from "../../../../components/LoadingOverlay";
import { ScrollArea } from "../../../../components/ScrollBar";
import StyledDialog from "../../../../components/StyledDialog";
import ContactInformation from "../../../../components/app/provider/patient/add-new-patient/ContactInformation";
import EmergencyContact from "../../../../components/app/provider/patient/add-new-patient/EmergencyContact";
import PatientDetail from "../../../../components/app/provider/patient/add-new-patient/PatientDetail";
import {
  ADD_NEW_PATIENT_INITIAL_VALUES,
  INITIAL_EM_CONTACT,
  INITIAL_PATIENT_INSURANCE,
  NewPatientForm,
  PATIENT_FORM_VALIDATION_SCHEMA,
} from "../../../../constants/provider/patient/add-new-patient/add-new-patient-form";
import useCurrentUser from "../../../../hooks/local-storage/use-current-user";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { useEditPatient } from "../../../../queries/use-edit-patient";
import { useGetPatient } from "../../../../queries/use-get-patient-uuid";
import { useLanguageList } from "../../../../queries/use-language-list";
import { useChangePatientAvatar } from "../../../../queries/use-patient-change-avatar";
import { PatientInsurance } from "../../../../services";
import {
  DEFAULT_UUID_NAME_LIST,
  getErrorSnackbar,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
  getValueFromObj,
  transformMultiSelectFrontEndVal,
} from "../../../../utils/utils";
import { getStoredRealm } from "@/utils/auth-storage";

interface PatientPortalEditPatientFormProps {
  open: boolean;
  onClose: () => void;
}

type PrefillPatientData = Partial<NewPatientForm> & {
  languages?: Record<string, string>;
  patientInsurances?: PatientInsurance[];
};

const getPatientPrefillValues = (
  patientData?: PrefillPatientData
): NewPatientForm => ({
  ...ADD_NEW_PATIENT_INITIAL_VALUES,
  ...patientData,
  emergencyContacts: patientData?.emergencyContacts?.length
    ? patientData?.emergencyContacts
    : INITIAL_EM_CONTACT,
  patientInsurances: patientData?.patientInsurances?.length
    ? (patientData?.patientInsurances.map(
        (singleInsurance: PatientInsurance) => ({
          ...singleInsurance,
          provider:
            singleInsurance?.provider &&
            Object.keys(singleInsurance?.provider)?.length
              ? {
                  value: Object.keys(singleInsurance.provider)?.[0],
                  label: getValueFromObj(singleInsurance.provider),
                }
              : undefined,
          planId: singleInsurance?.planId
            ? {
                value: singleInsurance?.planId,
                label: singleInsurance?.planName,
              }
            : undefined,
          insurancePayer: {
            payerId: {
              value: singleInsurance?.insurancePayer?.payerId,
              label: singleInsurance?.insurancePayer?.payerName ?? "",
            },
          },
        })
      ) as any)
    : [INITIAL_PATIENT_INSURANCE],
  languages: transformMultiSelectFrontEndVal(
    DEFAULT_UUID_NAME_LIST,
    patientData?.languages
  ),
});

const PatientPortalEditPatientForm = ({
  open,
  onClose,
}: PatientPortalEditPatientFormProps) => {
  const [currentUser, setCurrentUser] = useCurrentUser();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const { mutateAsync: editPatient } = useEditPatient();
  const { mutateAsync: changePatientAvatar } = useChangePatientAvatar();
  const xTENANTID = getStoredRealm();
  const [isLoading, setIsLoading] = useState(false);

  const { data: languageList } = useLanguageList({
    xTENANTID: xTENANTID ?? undefined,
  });

  const { data: patientDetails } = useGetPatient(currentUser?.uuid, !open);

  const prefillValues = useMemo(
    () =>
      getPatientPrefillValues(
        (patientDetails ?? currentUser) as PrefillPatientData | undefined
      ),
    [currentUser, patientDetails]
  );

  const formik = useFormik<NewPatientForm>({
    initialValues: prefillValues,
    validationSchema: PATIENT_FORM_VALIDATION_SCHEMA,
    enableReinitialize: true,
    onSubmit,
  });

  const syncCurrentUser = (patient: Record<string, unknown>) => {
    if (!currentUser) return;

    setCurrentUser({
      ...currentUser,
      ...patient,
    } as NonNullable<typeof currentUser>);
  };

  function onSubmit(values: NewPatientForm) {
    const date = values.birthDate ? new Date(values.birthDate) : undefined;
    if (date) date.setHours(0, 0, 0, 0);

    // ErrorBoundaryLogHelper(
    //   new Error(
    //     JSON.stringify({
    //       patientName: values?.firstName + "/" + values?.lastName,
    //       date: date?.toString(),
    //       birthDate: values?.birthDate,
    //       isoDate: date?.toISOString(),
    //     })
    //   )
    // );

    setIsLoading(true);
    const copiedValues: any = cloneDeep(values) as NewPatientForm & {
      uuid?: string;
      [key: string]: unknown;
    };
    copiedValues.firstName = copiedValues?.firstName?.trim();
    copiedValues.lastName = copiedValues?.lastName?.trim();
    copiedValues.middleName = copiedValues?.middleName?.trim();

    copiedValues.patientInsurances = values.patientInsurances?.map((it) => ({
      ...it,
      provider: it?.provider
        ? {
            [it?.provider.value]: `${it.provider.label}`,
          }
        : undefined,
      insurancePayer: {
        ...it.insurancePayer,
        payerId: it?.insurancePayer?.payerId.value,
      },
      planId: it?.planId?.value,
      planName: it?.planId?.label,
      birthDate: date?.toISOString(),
    }));

    copiedValues.assessments = {
      assessment: values.assessments?.assessment?.map((v) => v.value),
      scheduleTime: values.assessments?.recordedDate,
      reminderMedium: values.assessments?.reminderMedium
        ? [values.assessments?.reminderMedium]
        : undefined,
    };

    copiedValues.pharmacyLabRadiology = values.pharmacyLabRadiologyMap?.reduce(
      (acc, currVal) => {
        acc[currVal.uuid] = currVal.name;
        return acc;
      },
      {} as Record<string, string>
    );

    const transFormData = getUnNecessaryTransformData(["languages"], {
      ...copiedValues,
      birthDate: date?.toISOString(),
    }) as Record<string, unknown>;

    const payload = {
      patient: transFormData,
      xTENANTID: xTENANTID ?? undefined,
    };

    return editPatient(payload as any)
      .then((res) => {
        queryClient.invalidateQueries({
          queryKey: ["provider-group-patients-list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["patient"],
        });

        const onSuccessClose = () => {
          syncCurrentUser({
            ...transFormData,
            avatar: values.avatar,
          });
          showSnackbar(getSuccessSnackbar(res));
          formik.resetForm();
          onClose();
        };

        if (!values?.avatar?.includes(".amazonaws.com")) {
          return changePatientAvatar({
            xTENANTID: xTENANTID ?? "",
            avatar: {
              newAvatar: values.avatar,
              userId: copiedValues.uuid ?? "",
            },
          }).then((avatarResp) => {
            queryClient.invalidateQueries({
              queryKey: ["patient", copiedValues.uuid],
            });
            onSuccessClose();

            if (isEqual(omit(values, "avatar"), omit(copiedValues, "avatar"))) {
              return avatarResp;
            }

            return res;
          });
        }

        onSuccessClose();
        return res;
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)))
      .finally(() => setIsLoading(false));
  }

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      dialogTitle="Edit Profile"
      maxWidth={false}
      dialogAction={
        <Box
          display={"flex"}
          justifyContent={"flex-end"}
          gap={2}
          width={"100%"}
        >
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => formik.submitForm()}>
            Save
          </Button>
        </Box>
      }
    >
      <Box width={"min(1100px, 88vw)"}>
        <LoadingOverlay isLoading={isLoading} />
        <ScrollArea height={"62vh"}>
          <Box mt={1}>
            <PatientDetail
              formik={formik}
              languageList={languageList?.data?.content ?? []}
            />
            <ContactInformation formik={formik} />
            <EmergencyContact formik={formik} />
          </Box>
        </ScrollArea>
      </Box>
    </StyledDialog>
  );
};

export default PatientPortalEditPatientForm;
